import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import moment from 'moment';
import TEZPORT_LOGO from '../../images/LogoPrimary.svg';
import { containerConditionsLabel } from '../../const';
import { i18n } from '../../i18n';
import { ContainerGetOneQuery } from '../../types';
import { formatContainerType, formatContainerPlace } from '../../utils';

const printGlobalStyles = `
  @media print {
    @page {
      size: 80mm 290mm;
      margin: 0;
    }

    html,
    body {
      width: 80mm !important;
      margin: 0 !important;
      background: #ffffff !important;
    }

    #root {
      display: none !important;
    }

    #gate-receipt-print {
      display: block !important;
    }
  }
`;

const Root = styled.div`
  display: none;

  @media print {
    display: block;
    width: 80mm;
    color: #000000;
    background: #ffffff;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 8pt;
    line-height: 1.15;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
`;

const Page = styled.section<{ $back?: boolean }>`
  box-sizing: border-box;
  width: 80mm;
  min-height: 290mm;
  padding: 3.5mm 4mm;
  background: #ffffff;
  break-after: ${(props) => (props.$back ? 'auto' : 'page')};
  page-break-after: ${(props) => (props.$back ? 'auto' : 'always')};
`;

const Header = styled.header`
  display: grid;
  justify-items: center;
  gap: 0.6mm;
  padding-bottom: 1.8mm;
  border-bottom: 0.35mm solid #000000;
  text-align: center;
`;

const CompanyLogo = styled.img`
  display: block;
  max-width: 32mm;
  height: 10mm;
  object-fit: contain;
  filter: grayscale(1) contrast(1.35);
`;

const FallbackBrand = styled.div<{ $large?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${(props) => (props.$large ? '4mm' : '2mm')};

  img {
    width: ${(props) => (props.$large ? '25mm' : '12mm')};
    height: ${(props) => (props.$large ? '17mm' : '8mm')};
    object-fit: contain;
    filter: grayscale(1) contrast(2);
  }

  span {
    font-size: ${(props) => (props.$large ? '24pt' : '15pt')};
    font-weight: 900;
    letter-spacing: 0.08em;
  }
`;

const CompanyName = styled.div`
  font-size: 10pt;
  font-weight: 800;
`;

const CompanyAddress = styled.div`
  max-width: 68mm;
  font-size: 7pt;
`;

const CompanyRegistration = styled.div`
  font-size: 7pt;
`;

const CompanySlogan = styled.div`
  margin-top: 0.5mm;
  font-size: 7pt;
  font-style: italic;
  font-weight: 800;
`;

const CompanyGroup = styled.div`
  font-size: 6.5pt;
  font-style: italic;
`;

const ReceiptTitle = styled.div`
  margin-top: 2mm;
  padding: 2.2mm 1.5mm;
  background: #000000;
  color: #ffffff;
  font-size: 10pt;
  font-weight: 900;
  text-align: center;
  text-transform: uppercase;
`;

const Movement = styled.div`
  display: grid;
  grid-template-columns: 1fr max-content 1fr;
  align-items: center;
  margin-top: 2mm;
  padding: 1.8mm 3mm;
  border: 0.35mm solid #000000;
  font-size: 15pt;
  font-weight: 900;
  text-align: center;
`;

const MovementCaption = styled.div`
  padding: 1mm 0 1.5mm;
  font-size: 8pt;
  text-align: center;
`;

const Field = styled.div`
  padding: 1.2mm 0;
  border-bottom: 0.25mm solid #999999;
`;

const FieldLabel = styled.div`
  margin-bottom: 0.4mm;
  font-size: 7pt;
`;

const FieldValue = styled.div<{ $large?: boolean }>`
  min-height: 3.6mm;
  font-size: ${(props) => (props.$large ? '11pt' : '9pt')};
  font-weight: 800;
  overflow-wrap: anywhere;
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;

  & > ${Field}:nth-child(odd) {
    padding-right: 3mm;
    border-right: 0.25mm solid #999999;
  }

  & > ${Field}:nth-child(even) {
    padding-left: 3mm;
  }
`;

const SectionTitle = styled.div`
  margin-top: 1.7mm;
  padding: 1mm 0 0.8mm;
  border-bottom: 0.35mm solid #000000;
  font-size: 8pt;
  font-weight: 900;
  text-transform: uppercase;
`;

const LocationSectionTitle = styled(SectionTitle)`
  border-bottom: 0;
`;

const ContainerNumber = styled.div`
  padding: 1.7mm 0;
  border-bottom: 0.25mm solid #999999;
  font-size: 17pt;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-align: center;
`;

const ConditionBox = styled.div`
  margin-top: 1.7mm;
  padding: 1.7mm 2mm;
  border: 0.35mm solid #000000;
  font-size: 10pt;
  font-weight: 900;
  text-align: center;
`;

const Remarks = styled.div`
  margin-top: 1.5mm;
  padding: 1.5mm 2mm;
  border: 0.35mm solid #000000;
`;

const RemarksMeta = styled.div`
  margin-top: 1mm;
  font-size: 7pt;
`;

const ReeferDetails = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 2mm;
  padding: 1.3mm 0;
  border-bottom: 0.25mm solid #999999;
  font-size: 7pt;
`;

const LocationBox = styled.div`
  margin-top: 1.5mm;
  padding: 1.6mm 2mm 1.2mm;
  border: 0.35mm solid #000000;
  text-align: center;
`;

const LocationValue = styled.div`
  font-size: 14pt;
  font-weight: 900;
  letter-spacing: 0.04em;
`;

const LocationHint = styled.div`
  margin-top: 0.5mm;
  font-size: 7pt;
`;

const Terms = styled.div`
  padding: 1.5mm 0;
  font-size: 6.5pt;
`;

const Signatures = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5mm;
  padding-top: 1.5mm;
  border-top: 0.35mm solid #000000;
  font-size: 7pt;
`;

const Stamp = styled.div`
  width: max-content;
  max-width: 50mm;
  margin: 2mm auto 0;
  padding: 1.2mm 4mm;
  border: 0.6mm solid #000000;
  transform: rotate(-4deg);
  font-size: 11pt;
  font-weight: 900;
  text-align: center;
  text-transform: uppercase;
`;

const StampCity = styled.div`
  font-size: 7pt;
`;

const BackPage = styled(Page)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const BackCompanyLogo = styled(CompanyLogo)`
  width: auto;
  max-width: 60mm;
  height: auto;
  max-height: 55mm;
`;

const BackCompanyName = styled.div`
  max-width: 64mm;
  margin-top: 6mm;
  font-size: 16pt;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const BackAddress = styled.div`
  max-width: 62mm;
  margin-top: 4mm;
  padding-top: 3mm;
  border-top: 0.4mm solid #000000;
  font-size: 9pt;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

interface GateReceiptPrintProps {
  container: ContainerGetOneQuery;
  onPrinted: () => void;
}

interface GateReceiptData {
  id?: string;
  completedAt?: number | string | Date;
  releaseNumber?: string;
  comment?: string;
  images?: { url: string }[];
  driver?: {
    firstName?: string;
    lastName?: string;
    licensePlateNumber?: string;
    phoneNumber?: string;
  };
}

type GateReceiptType = 'gate-in' | 'gate-out';

const emptyValue = '—';

const formatPhoneNumber = (phoneNumber?: string) => {
  if (!phoneNumber) return emptyValue;

  const normalizedPhoneNumber = phoneNumber.trim();
  if (!normalizedPhoneNumber) return emptyValue;

  return normalizedPhoneNumber.startsWith('+')
    ? normalizedPhoneNumber
    : `+${normalizedPhoneNumber}`;
};

const GateReceiptPrint = ({
  container,
  onPrinted,
  type,
}: GateReceiptPrintProps & { type: GateReceiptType }) => {
  const hasPrinted = useRef(false);
  const isGateOut = type === 'gate-out';
  const companyLogoUrl = container.clientYard?.yard?.yardCompany?.logo?.url;
  const [useFallbackLogo, setUseFallbackLogo] = useState(!companyLogoUrl);

  const gateRequest = (isGateOut ? container.gateOut : container.gateIn) as GateReceiptData;
  const completedAt = gateRequest?.completedAt;
  const yard = container.clientYard?.yard;
  const city = yard?.address?.city || emptyValue;
  const companyName = yard?.yardCompany?.legalName || i18n.t('pages.gateInReceipt.tezport');
  const address = [
    yard?.name,
    yard?.address?.streetAddress,
    yard?.address?.city,
    yard?.address?.zipCode,
    yard?.address?.country,
  ]
    .filter(Boolean)
    .join(' - ');
  const driverName = [gateRequest?.driver?.firstName, gateRequest?.driver?.lastName]
    .filter(Boolean)
    .join(' ');
  const condition = container.condition
    ? containerConditionsLabel[container.condition]
    : emptyValue;
  const location = formatContainerPlace(container) || emptyValue;
  const receiptNumber = gateRequest?.id
    ? `${isGateOut ? 'GO' : 'GI'}-${
        completedAt ? moment(completedAt).format('YYMMDD') : '000000'
      }-${gateRequest.id.slice(-6).toUpperCase()}`
    : emptyValue;
  const loadStatus =
    container.isLoaded === true
      ? isGateOut
        ? i18n.t('pages.gateOutReceipt.full')
        : i18n.t('pages.gateInReceipt.loaded')
      : container.isLoaded === false
      ? i18n.t('pages.gateInReceipt.empty')
      : emptyValue;
  const movementCaption =
    container.isLoaded === true
      ? isGateOut
        ? i18n.t('pages.gateOutReceipt.fullExport')
        : i18n.t('pages.gateInReceipt.loadedReturn')
      : container.isLoaded === false
      ? isGateOut
        ? i18n.t('pages.gateOutReceipt.emptyExport')
        : i18n.t('pages.gateInReceipt.emptyReturn')
      : emptyValue;
  const printTitle = isGateOut
    ? i18n.t('pages.gateOutReceipt.printTitle')
    : i18n.t('pages.gateInReceipt.printTitle');
  const clerkName = isGateOut ? container.gatedOutByName : container.gatedInByName;
  const photoCount = isGateOut ? gateRequest?.images?.length || 0 : container.images?.length || 0;
  const customLogoAvailable = !!companyLogoUrl && !useFallbackLogo;

  useEffect(() => {
    if (hasPrinted.current) return;
    hasPrinted.current = true;
    let cancelled = false;

    const preloadImage = (src: string) =>
      new Promise<boolean>((resolve) => {
        const image = new Image();
        image.onload = () => resolve(true);
        image.onerror = () => resolve(false);
        image.src = src;
      });

    const print = async () => {
      if (companyLogoUrl && !(await preloadImage(companyLogoUrl))) {
        setUseFallbackLogo(true);
        await preloadImage(TEZPORT_LOGO);
      } else if (!companyLogoUrl) {
        await preloadImage(TEZPORT_LOGO);
      }

      if (document.fonts?.ready) await document.fonts.ready;
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
      );
      if (cancelled) return;

      const previousTitle = document.title;
      document.title = `${printTitle} - ${container.number}`;
      window.print();
      document.title = previousTitle;
      onPrinted();
    };

    print();

    return () => {
      cancelled = true;
    };
  }, [companyLogoUrl, container.number, onPrinted, printTitle]);

  const brand = (large = false) =>
    customLogoAvailable ? (
      large ? (
        <BackCompanyLogo src={companyLogoUrl} alt={companyName} />
      ) : (
        <CompanyLogo src={companyLogoUrl} alt={companyName} />
      )
    ) : (
      <FallbackBrand $large={large}>
        <img src={TEZPORT_LOGO} alt={i18n.t('pages.gateInReceipt.tezport')} />
        <span>{i18n.t('pages.gateInReceipt.tezport')}</span>
      </FallbackBrand>
    );

  return createPortal(
    <>
      <style>{printGlobalStyles}</style>
      <Root id={'gate-receipt-print'}>
        <Page>
          <Header>
            {brand()}
            <CompanyName>{companyName}</CompanyName>
            <CompanyAddress>{address || emptyValue}</CompanyAddress>
            <CompanyRegistration>
              {i18n.t('pages.gateInReceipt.companyRegistration')}
            </CompanyRegistration>
            <CompanySlogan>{i18n.t('pages.gateInReceipt.companySlogan')}</CompanySlogan>
            <CompanyGroup>{i18n.t('pages.gateInReceipt.companyGroup')}</CompanyGroup>
          </Header>

          <ReceiptTitle>{i18n.t('pages.gateInReceipt.equipmentInterchangeReceipt')}</ReceiptTitle>
          <Movement>
            <span>
              {isGateOut
                ? i18n.t('pages.gateOutReceipt.gateOut')
                : i18n.t('pages.gateInReceipt.gateIn')}
            </span>
            <span>→</span>
            <span>
              {isGateOut ? i18n.t('pages.gateOutReceipt.out') : i18n.t('pages.gateInReceipt.in')}
            </span>
          </Movement>
          <MovementCaption>{movementCaption}</MovementCaption>

          <Field>
            <FieldLabel>{i18n.t('pages.gateInReceipt.eirNumber')}</FieldLabel>
            <FieldValue $large>{receiptNumber}</FieldValue>
          </Field>
          <Columns>
            <Field>
              <FieldLabel>{i18n.t('pages.gateInReceipt.date')}</FieldLabel>
              <FieldValue>
                {completedAt ? moment(completedAt).format('YYYY-MM-DD') : emptyValue}
              </FieldValue>
            </Field>
            <Field>
              <FieldLabel>{i18n.t('pages.gateInReceipt.time')}</FieldLabel>
              <FieldValue>
                {completedAt ? moment(completedAt).format('HH:mm:ss') : emptyValue}
              </FieldValue>
            </Field>
            <Field>
              <FieldLabel>{i18n.t('pages.gateInReceipt.lane')}</FieldLabel>
              <FieldValue>
                {isGateOut
                  ? i18n.t('pages.gateInReceipt.defaultGateOutLane')
                  : i18n.t('pages.gateInReceipt.defaultGateInLane')}
              </FieldValue>
            </Field>
            <Field>
              <FieldLabel>{i18n.t('pages.gateInReceipt.clerk')}</FieldLabel>
              <FieldValue>{clerkName || emptyValue}</FieldValue>
            </Field>
          </Columns>

          <SectionTitle>{i18n.t('pages.gateInReceipt.container')}</SectionTitle>
          <ContainerNumber>{container.number}</ContainerNumber>
          <Columns>
            <Field>
              <FieldLabel>{i18n.t('pages.gateInReceipt.sizeType')}</FieldLabel>
              <FieldValue>{formatContainerType(container.type) || emptyValue}</FieldValue>
            </Field>
            <Field>
              <FieldLabel>{i18n.t('pages.gateInReceipt.shippingLine')}</FieldLabel>
              <FieldValue>{i18n.t('pages.gateInReceipt.defaultShippingLine')}</FieldValue>
            </Field>
          </Columns>
          <Columns>
            <Field>
              <FieldLabel>{i18n.t('pages.gateInReceipt.status')}</FieldLabel>
              <FieldValue>{loadStatus}</FieldValue>
            </Field>
            <Field>
              <FieldLabel>{i18n.t('pages.gateInReceipt.seal')}</FieldLabel>
              <FieldValue>{emptyValue}</FieldValue>
            </Field>
          </Columns>
          <Columns>
            <Field>
              <FieldLabel>{i18n.t('pages.gateInReceipt.category')}</FieldLabel>
              <FieldValue>{i18n.t('pages.gateInReceipt.defaultCategory')}</FieldValue>
            </Field>
            <Field>
              <FieldLabel>{i18n.t('pages.gateInReceipt.serviceRequired')}</FieldLabel>
              <FieldValue>{i18n.t('pages.gateInReceipt.defaultServiceRequired')}</FieldValue>
            </Field>
          </Columns>
          <Field>
            <FieldLabel>{i18n.t('pages.gateInReceipt.bookingReturnReference')}</FieldLabel>
            <FieldValue>{gateRequest?.releaseNumber || emptyValue}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{i18n.t('pages.gateInReceipt.customer')}</FieldLabel>
            <FieldValue>
              {container.clientYard?.client?.legalName ||
                container.clientYard?.client?.code ||
                emptyValue}
            </FieldValue>
          </Field>

          <SectionTitle>{i18n.t('pages.gateInReceipt.truckAndDriver')}</SectionTitle>
          <Columns>
            <Field>
              <FieldLabel>{i18n.t('pages.gateInReceipt.truck')}</FieldLabel>
              <FieldValue>{gateRequest?.driver?.licensePlateNumber || emptyValue}</FieldValue>
            </Field>
            <Field>
              <FieldLabel>{i18n.t('pages.gateInReceipt.mobile')}</FieldLabel>
              <FieldValue>{formatPhoneNumber(gateRequest?.driver?.phoneNumber)}</FieldValue>
            </Field>
          </Columns>
          <Field>
            <FieldLabel>{i18n.t('pages.gateInReceipt.driver')}</FieldLabel>
            <FieldValue>{driverName || emptyValue}</FieldValue>
          </Field>

          <SectionTitle>{i18n.t('pages.gateInReceipt.condition')}</SectionTitle>
          <ConditionBox>{condition}</ConditionBox>
          <Remarks>
            <FieldLabel>{i18n.t('pages.gateInReceipt.remarks')}</FieldLabel>
            <FieldValue>{gateRequest?.comment || container.comment || emptyValue}</FieldValue>
            <RemarksMeta>
              {i18n.t('pages.gateInReceipt.photos')}: {photoCount} ·{' '}
              {i18n.t('pages.gateInReceipt.inspector')}: {clerkName || emptyValue}
            </RemarksMeta>
          </Remarks>

          <ReeferDetails>
            <span>{i18n.t('pages.gateInReceipt.reeferTemperatures')}</span>
            <span>{i18n.t('pages.gateInReceipt.dryOnly')}</span>
          </ReeferDetails>

          <LocationSectionTitle>{i18n.t('pages.gateInReceipt.yardLocation')}</LocationSectionTitle>
          <LocationBox>
            <LocationValue>{location}</LocationValue>
            <LocationHint>{i18n.t('pages.gateInReceipt.yardLocationHint')}</LocationHint>
          </LocationBox>

          <Terms>
            {isGateOut
              ? i18n.t('pages.gateOutReceipt.releasedCondition')
              : i18n.t('pages.gateInReceipt.receivedCondition')}
            <br />
            {i18n.t('pages.gateInReceipt.damageClaimsDeadline')}
            <br />
            {i18n.t('pages.gateInReceipt.notValidForPickup')}
            <br />
            {i18n.t('pages.gateInReceipt.checkDocuments')}
          </Terms>
          <Signatures>
            <span>{i18n.t('pages.gateInReceipt.driverSignature')}: __________</span>
            <span>{i18n.t('pages.gateInReceipt.gateClerkSignature')}: __________</span>
          </Signatures>
          <Stamp>
            {yard?.name || companyName}
            <br />
            {isGateOut
              ? i18n.t('pages.gateOutReceipt.gateOut')
              : i18n.t('pages.gateInReceipt.gateIn')}
            <StampCity>{city}</StampCity>
          </Stamp>
        </Page>

        <BackPage $back>
          {brand(true)}
          <BackCompanyName>{companyName}</BackCompanyName>
          <BackAddress>{address || emptyValue}</BackAddress>
        </BackPage>
      </Root>
    </>,
    document.body,
  );
};

export const GateInReceiptPrint = (props: GateReceiptPrintProps) => (
  <GateReceiptPrint {...props} type={'gate-in'} />
);

export const GateOutReceiptPrint = (props: GateReceiptPrintProps) => (
  <GateReceiptPrint {...props} type={'gate-out'} />
);
